import { basePath } from "@/config";
import { getItemWithCrypto } from "@/lib/auth/crypto-storage";
import { paths } from "@/paths";
import { useAuthExpiredStore } from "@/contexts/auth-expired-store";

export const baseApiUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

// 响应解析器接口
interface ResponseParser {
  matcher: (contentType: string) => boolean;
  parser: (response: Response) => Promise<any>;
}

// 定义响应解析器
const responseParsers: ResponseParser[] = [
  {
    matcher: (contentType) => contentType.includes("application/json"),
    parser: (res) => res.json(),
  },
  {
    matcher: (contentType) => contentType.startsWith("text/"),
    parser: (res) => res.text(),
  },
];

// 解析响应内容
async function parseResponse(res: Response): Promise<any> {
  const contentType = res.headers.get("content-type") || "";

  // 查找匹配的解析器
  const parser = responseParsers.find((p) => p.matcher(contentType));

  try {
    // 如果找到匹配的解析器就使用它，否则默认使用 json 解析
    const data = await (parser ? parser.parser(res) : res.json());
    return data;
  } catch (e: unknown) {
    if (e instanceof Error) {
      throw new Error(`Failed to parse response: ${e.message}`);
    }
    throw new Error("Failed to parse response: Unknown error");
  }
}

export interface HttpRes<T> {
  code: number;
  result: T;
  message: string;
}

export const getAuthToken = () => {
  // 仅在客户端环境执行（避免 SSR 报错）
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split("; ");
  const tokenCookie = cookies.find((cookie) => cookie.startsWith("token="));
  return tokenCookie ? tokenCookie.split("=")[1] : null;
};

// 判断是否为测试环境（包括 Storybook）
export const isTestEnvironment = () => {
  return process.env.NODE_ENV === "test" || process.env.STORYBOOK === "true";
};

export async function fetchWithAuth<T>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  // 在测试环境使用测试 token，其他环境使用真实 token
  const token = isTestEnvironment() ? "test-token" : getAuthToken();

  // 构建请求头
  const headers = {
    ...options?.headers,
    "Content-Type": "application/json",
    Authorization: `JWT ${token}`,
  };

  return fetch(baseApiUrl + url, {
    ...options,
    headers,
  }).then(async (res) => {
    // 处理 403 错误（非测试环境）
    // 不判断本地 toekn, 由服务端鉴权
    if (res.status === 403 && !isTestEnvironment()) {
      useAuthExpiredStore.getState().setExpired(true);
      return Promise.reject("Access denied (403)");
    }

    try {
      const data = await parseResponse(res);
      const contentType = res.headers.get("content-type") || "";

      // 只有当响应是 JSON 时才检查 code 字段
      if (contentType.includes("application/json")) {
        if (data.code === 200) {
          return data.result;
        } else {
          return Promise.reject(data.message || "Unknown error");
        }
      }

      // 非 JSON 响应直接返回解析后的数据
      return data;
    } catch (e: unknown) {
      if (e instanceof Error) {
        return Promise.reject(`Parse response failed: ${e.message}`);
      }
      return Promise.reject("Parse response failed: Unknown error");
    }
  });
}

/**
 * Fetches a stream from the API with authentication
 * @template T The type of chunks in the stream
 * @param url The API endpoint URL
 * @param options Request options
 * @param onChunk Callback function to handle each chunk of data
 * @returns Promise that resolves when the stream is complete
 */
export async function fetchStreamWithAuth<T>(
  url: string,
  options?: RequestInit,
  onChunk?: (chunk: T) => void,
  onFinish?: () => void,
): Promise<void> {
  const token = isTestEnvironment() ? "test-token" : getAuthToken();

  const headers = {
    ...options?.headers,
    Accept: "text/event-stream",
    Authorization: `JWT ${token}`,
  };

  let incompleteChunk = "";
  try {
    const response = await fetch(baseApiUrl + url, {
      ...options,
      headers,
    });

    // debug
    // const response = await fetch(basePath + "/api/stream");

    if (!response.ok) {
      if (response.status === 403 && !isTestEnvironment()) {
        useAuthExpiredStore.getState().setExpired(true);
        throw new Error("Access denied (403)");
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error("No response body received");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // 处理最后可能残留的不完整数据
        if (incompleteChunk) {
          try {
            const parsedChunk = JSON.parse(incompleteChunk);
            onChunk?.(parsedChunk);
          } catch (e) {
            console.error("Failed to parse final chunk:", incompleteChunk, e);
          }
        }
        onFinish?.();
        break;
      }

      if (value) {
        const buffer = decoder.decode(value, { stream: true });
        const lines = (incompleteChunk + buffer).split("\n");

        // 保存最后一行，因为它可能不完整
        incompleteChunk = lines[lines.length - 1];

        // 处理除最后一行外的所有完整行
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i];
          if (!line.trim()) continue;

          const chunk = line.replace(/^data: /, "");
          try {
            const parsedChunk = JSON.parse(chunk);
            onChunk?.(parsedChunk);
          } catch (e) {
            console.error("Failed to parse JSON:", chunk, e);
          }
        }
      }
    }
  } catch (error) {
    console.error("Stream error:", error);
    throw error;
  }
}
